/**
 * @file CreatePrivateChatButton.js
 * @author Simon Tenedero, Jonas Matulis
 * @created 2024-XX-XX
 * @lastModified 2025-05-27
 * @description file containing CreatePrivateChatButton component
 */

import { useState, useEffect } from "react";
import ChatCreationModal from "../Modal/ChatCreationModal.js";

import { createChat } from "../../utils/privateChatUtils";
import { useUser } from "../../context/UserContext";

const MAX_PRIVATE_CHATS = 5; // Maximum number of private chats allowed

/**
 * This component is a button that allows users to create a new private chat.
 *
 * @param {Object} props - The component properties
 * @param {Array} props.privateChats - Array of private chat objects.
 * @param {Function} props.setSelectedPrivateChat - Callback function to handle chat selection after creation (navigate to new chat as soon as it is created).
 * @param {Function} props.setSelectedPrivateTab - Function to set the selected private tab.
 * @param {Function} props.setSelectedPrivateChatMembers - Function to set the selected private chat members.
 * @param {Array} props.privateChatMembers - Array of private chat member objects.
 * @param {Function} props.setSelectedPrivateChatPendingMembers - Function to set the selected private chat pending members.
 * @param {Array} props.pendingPrivateChatMembers - Array of pending private chat member objects.
 *
 * @returns {JSX.Element} The rendered CreatePrivateChatButton component
 */
const CreatePrivateChatButton = ({
  privateChats,
  setSelectedPrivateChat, // Callback to select the chat after creation
  setSelectedPrivateTab, // Function to set the selected private tab
  setSelectedPrivateChatMembers,
  privateChatMembers,
  setSelectedPrivateChatPendingMembers,
  pendingPrivateChatMembers,
}) => {
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const { user } = useUser();
  const [pendingChatId, setPendingChatId] = useState(null);

  /**
   * Handles the creation of a new chat if the limit has not been reached.
   * @param {Object} chatSettings - Settings for the new chat.
   */
  const handleCreateChat = async (chatSettings) => {
    console.log("Creating chat with settings:", chatSettings);
    if (privateChats.length < MAX_PRIVATE_CHATS) {
      const chatId = await createChat(chatSettings, user); // Create new chat
      setPendingChatId(chatId);
    } else {
      alert(`You can only create up to ${MAX_PRIVATE_CHATS} private chats.`);
    }
  };

  // Watch for the chat we just created to appear, so we can open it.
  useEffect(() => {
    if (pendingChatId && privateChats.length > 0) {
      const foundChat = privateChats.find((chat) => chat.id === pendingChatId);
      if (foundChat) {
        setSelectedPrivateChat(foundChat);
        setSelectedPrivateChatMembers(
          privateChatMembers.get(pendingChatId) || [],
        );
        setSelectedPrivateChatPendingMembers(
          pendingPrivateChatMembers?.get(pendingChatId) || [],
        );
        setPendingChatId(null); // Clear pending
        setSelectedPrivateTab("messagesTab"); // Switch to messages tab
      }
    }
  }, [
    privateChats,
    pendingChatId,
    privateChatMembers,
    pendingPrivateChatMembers,
  ]);

  /**
   * Sets and displays a notification message for a short period.
   * @param {string} message - Notification message to display.
   */
  /*
  const handleInviteNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(''), 5000); // Clear notification after 5 seconds
  };*/

  const [showChatCreationModal, setShowChatCreationModal] = useState(false); // State for showing chat creation menu

  return (
    <>
      {/* Login Alert Modal */}
      {showLoginAlert && (
        <div className="active-users-modal fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
          <div className="modal-content bg-primary rounded-lg shadow-lg p-4 w-full max-w-md dark:bg-gray-800">
            <span
              className="close text-red-500 hover:text-red-800 cursor-pointer float-right"
              onClick={() => setShowLoginAlert(false)}
            >
              &times;
            </span>
            <h2 className="text-current text-2xl font-semibold m-4 text-center">
              You must Login to create Private Chats!
            </h2>
          </div>
        </div>
      )}

      {/* Create Chat Button */}
      <div className="flex flex-col space-y-2 p-2">
        <button
          onClick={() => {
            setShowChatCreationModal(true);
            if (!user) {
              setShowLoginAlert(true);
            }
          }}
          className="justify-center flex text-nowrap text-center text-black w-full btn bg-accent hover:bg-white"
        >
          create chat
        </button>
      </div>

      {/* Chat Creation Menu */}
      {showChatCreationModal && (
        <ChatCreationModal
          onCreateChat={handleCreateChat}
          onClose={() => setShowChatCreationModal(false)}
          currentUser={user}
          showLoginAlert={showLoginAlert}
          setShowLoginAlert={setShowLoginAlert}
        />
      )}
    </>
  );
};

export default CreatePrivateChatButton;
